export type Direction = 'Import' | 'Export';

export type JurisdictionId =
  | 'US Import'
  | 'EU Import'
  | 'CA Import'
  | 'US Export';

export interface Jurisdiction {
  id: JurisdictionId;
  tradeType: string;
  label: string;
  region: string;
  country: string;
  noun: string;
  htsFilters: boolean;
  countsRecords: boolean;
  stampsTicket: boolean;
  /** the evaluator only knows US trade categories */
  showsTradeCategory: boolean;
  /** the Tariff page's country tab; null where the Tariff tool has no data */
  tariffCountry: 'us' | 'ca' | null;
}

export interface Partner {
  key: 'origin' | 'destination';
  label: string;
  prep: 'from' | 'to';
  jurisdictionPrep: 'into' | 'from';
  search: string;
}

export interface ScreeningProfile {
  direction: Direction;
  index: string;
  jurisdictions: Jurisdiction[];
  partner: Partner;
  nextStep: { to: string; label: string };
  copy: {
    title: string;
    lede: string;
    help: string;
    burden: string;
  };
}

const BURDEN_NEEDS =
  'It comes from the same evaluation as risk, so it needs the same two inputs.';

export const IMPORT_PROFILE: ScreeningProfile = {
  direction: 'Import',
  index: 'bite_app_commodities_combined',
  jurisdictions: [
    {
      id: 'US Import',
      tradeType: 'US Import',
      label: 'US imports',
      region: 'United States',
      country: 'USA',
      noun: 'US import',
      htsFilters: true,
      countsRecords: true,
      stampsTicket: true,
      showsTradeCategory: true,
      tariffCountry: 'us',
    },
    {
      id: 'EU Import',
      tradeType: 'EU Import',
      label: 'EU imports',
      region: 'European Union',
      country: 'European Union',
      noun: 'EU import',
      htsFilters: false,
      countsRecords: false,
      stampsTicket: true,
      showsTradeCategory: false,
      tariffCountry: null,
    },
    {
      id: 'CA Import',
      tradeType: 'CA Import',
      label: 'Canada imports',
      region: 'Canada',
      country: 'Canada',
      noun: 'Canada import',
      htsFilters: false,
      countsRecords: false,
      stampsTicket: true,
      showsTradeCategory: false,
      tariffCountry: 'ca',
    },
  ],
  partner: {
    key: 'origin',
    label: 'Origin',
    prep: 'from',
    jurisdictionPrep: 'into',
    search: 'Search countries',
  },
  nextStep: { to: '/tariff-checker', label: 'Check tariffs' },
  copy: {
    title: 'Screen an import',
    lede: 'Match a product against the import lists for its destination, by HTS code or description.',
    help: 'Enter a product name or HTS code, add a country of origin, and narrow the screen with list or HTS code filters.',
    burden: `Duty, licensing and paperwork for this route. ${BURDEN_NEEDS}`,
  },
};

export const EXPORT_PROFILE: ScreeningProfile = {
  direction: 'Export',
  index: 'bite_app_commodities_*',
  jurisdictions: [
    {
      id: 'US Export',
      tradeType: 'US Import',
      label: 'US exports',
      region: 'United States',
      country: 'USA',
      noun: 'export',
      htsFilters: true,
      countsRecords: false,
      stampsTicket: false,
      showsTradeCategory: true,
      tariffCountry: 'us',
    },
  ],
  partner: {
    key: 'destination',
    label: 'Destination',
    prep: 'to',
    jurisdictionPrep: 'from',
    search: 'Search destinations',
  },
  nextStep: { to: '/bite-eccn', label: 'Check ECCN' },
  copy: {
    title: 'Screen an export',
    lede: 'Match a product against the export controls for where it is going, by HTS code or description.',
    help: 'Enter a product name or HTS code, add a country of destination, and narrow the screen with list or HTS code filters.',
    burden: `Licensing and paperwork for this route. ${BURDEN_NEEDS}`,
  },
};

const findJurisdiction = (
  profile: ScreeningProfile,
  id: string | undefined,
): Jurisdiction | undefined => profile.jurisdictions.find((j) => j.id === id);

export const jurisdictionOf = (
  profile: ScreeningProfile,
  id: string | undefined,
): Jurisdiction => findJurisdiction(profile, id) || profile.jurisdictions[0];

const routeEnds = <T>(
  profile: ScreeningProfile,
  jurisdictionEnd: T,
  partnerEnd: T,
): [T, T] =>
  profile.partner.key === 'origin'
    ? [partnerEnd, jurisdictionEnd]
    : [jurisdictionEnd, partnerEnd];

export const routeCountries = (
  profile: ScreeningProfile,
  jurisdiction: Jurisdiction,
  partner: string,
): { origin: string; destination: string } => {
  const [origin, destination] = routeEnds(
    profile,
    jurisdiction.country,
    partner,
  );
  return { origin: origin, destination: destination };
};

export const routeLabel = (
  profile: ScreeningProfile,
  jurisdiction: Jurisdiction,
  partner: string,
  unset = `${profile.partner.label} not set`,
): string =>
  routeEnds(profile, jurisdiction.region, partner || unset).join(' → ');

export const partnerPhrase = (
  profile: ScreeningProfile,
  partner: string,
): string => (partner ? ` ${profile.partner.prep} ${partner}` : '');

export const coverage = (
  profile: ScreeningProfile,
  jurisdiction: Jurisdiction,
): string =>
  `${profile.direction.toLowerCase()}s ${
    profile.partner.jurisdictionPrep
  } the ${jurisdiction.region}`;
